import { useEffect, useMemo, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { createPortal } from 'react-dom'

import { Avatar } from '../../../utils/avatar'
import { initTooltips } from '../../../utils/tooltips'

function uniqueById(users) {
  const map = new Map()
  users.forEach((u) => {
    if (u && u.id) map.set(String(u.id), u)
  })
  return Array.from(map.values())
}

function AvatarGroup({ users, max = 999 }) {
  if (!users || users.length === 0) return <div className="">No views</div>

  const visible = users.slice(0, max)
  const hiddenCount = Math.max(0, users.length - visible.length)

  return (
    <div className="avatar-group d-inline-flex align-items-center">
      {visible.map((u) => (
        <span
          key={u.id}
          className="avatar avatar-xs"
          aria-label={u.full_name || u.email}
          data-bs-toggle="tooltip"
          data-bs-title={u.full_name || u.email}
        >
          <img
            src={u.avatar_url}
            className="avatar-img rounded-circle"
            alt={u.full_name || u.email}
          />
        </span>
      ))}
      {hiddenCount > 0 ? <span className="small text-muted ms-2">+ {hiddenCount} more</span> : null}
    </div>
  )
}

function ChecklistTable({ entries, title, onOpenReport, selectedDayDate }) {
  if (!entries || entries.length === 0) return null

  return (
    <>
      <h4 className="card-title mt-3 mb-2">{title}</h4>
      <div className="table-responsive">
        <table className="table table-sm table-nowrap card-table">
          <thead>
            <tr>
              <th style={{ width: '25%' }}>User</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Seen By</th>
            </tr>
          </thead>
          <tbody className="fs-base">
            {entries.map((entry) => {
              const { user, report } = entry
              return (
                <tr key={user?.id || Math.random()}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <Avatar user={user} size="xs" />
                      <span>{user?.full_name || user?.email}</span>
                    </div>
                  </td>
                  <td>
                    {report ? (
                      <span className="badge bg-success-soft">Submitted</span>
                    ) : (
                      <span className="badge bg-danger-soft">Missing</span>
                    )}
                  </td>
                  <td>
                    {report ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => onOpenReport(report.id)}
                        >
                          View Daily Report
                        </button>
                        <div className="small text-muted">
                          {submittedAtLabel(new Date(report.applicable_date || selectedDayDate), report.created_at)}
                          {' / '}
                          {submittedAtTimestamp(report.created_at)}
                        </div>
                      </>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    {report ? (
                      <AvatarGroup users={report.viewers || []} max={4} />
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

function submittedAtLabel(applicableDate, createdAtIso) {
  const createdAt = new Date(createdAtIso)
  const endOfDay = new Date(applicableDate)
  endOfDay.setDate(endOfDay.getDate() + 1)
  endOfDay.setHours(23, 59, 59, 999)
  if (createdAt <= endOfDay) return 'within 1 day'

  const diffMs = createdAt.getTime() - applicableDate.getTime()
  const mins = Math.round(diffMs / 60000)
  if (mins < 60) return `after ${mins} minutes`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `after ${hours} hours`
  const days = Math.round(hours / 24)
  return `after ${days} days`
}

function daysAgoLabel(dateStr) {
  const date = new Date(dateStr)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const target = new Date(date)
  target.setHours(0, 0, 0, 0)

  const diffMs = today.getTime() - target.getTime()
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 0) return 'in the future'
  return `${diffDays} days ago`
}

function isFutureDateStr(dateStr) {
  const date = new Date(dateStr + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date.getTime() > today.getTime()
}

function isTodayDateStr(dateStr) {
  const date = new Date(dateStr + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date.getTime() === today.getTime()
}

function buildMonthGrid(year, month, daysByDate) {
  const firstDay = new Date(year, month - 1, 1)
  const lastDay = new Date(year, month, 0)
  const daysInMonth = lastDay.getDate()
  const startDayOfWeek = firstDay.getDay() // 0 = Sunday
  const offset = (startDayOfWeek + 6) % 7 // Monday-first offset

  const prevMonthLastDay = new Date(year, month - 1, 0).getDate()

  const cells = []

  // Previous month padding
  for (let i = offset - 1; i >= 0; i--) {
    cells.push({
      date: null,
      dayNumber: prevMonthLastDay - i,
      isPadding: true,
      isToday: false,
      isFuture: false,
      isWeekend: false,
    })
  }

  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    const dateObj = new Date(dateStr + 'T00:00:00')
    const dayOfWeek = dateObj.getDay()
    cells.push({
      date: dateStr,
      dayNumber: d,
      isPadding: false,
      isToday: isTodayDateStr(dateStr),
      isFuture: isFutureDateStr(dateStr),
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
      day: daysByDate.get(dateStr),
    })
  }

  // Next month padding to fill grid rows
  const totalCells = cells.length
  const rows = Math.ceil(totalCells / 7)
  const targetCells = rows * 7
  const remaining = targetCells - totalCells
  for (let i = 1; i <= remaining; i++) {
    cells.push({
      date: null,
      dayNumber: i,
      isPadding: true,
      isToday: false,
      isFuture: false,
      isWeekend: false,
    })
  }

  return cells
}

async function fetchOnlineUsers() {
  const response = await fetch('/users/online.json', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  if (!response.ok) throw new Error(`Failed to load online users: ${response.status}`)
  return response.json()
}

function applyUserFilterToData(data, selectedUserId) {
  if (!data) return data
  if (!selectedUserId) return data

  const idStr = String(selectedUserId)
  const nextDays = data.days.map((day) => {
    const entries = (day.entries || []).filter((e) => String(e.user?.id) === idStr)
    const reports = entries.map((e) => e.report).filter(Boolean)
    const viewers = uniqueById(reports.flatMap((r) => r.viewers || []))
    return {
      ...day,
      entries,
      reports,
      reports_count: reports.length,
      viewers,
    }
  })

  return {
    ...data,
    days: nextDays,
    selected_user_id: selectedUserId,
  }
}

async function fetchDailyReport({ id }) {
  const response = await fetch(`/users/daily_reports/${id}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  if (!response.ok) throw new Error(`Failed to load report: ${response.status}`)
  return response.json()
}

function submittedAtTimestamp(createdAtIso) {
  const createdAt = new Date(createdAtIso)
  if (Number.isNaN(createdAt.getTime())) return ''

  return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(createdAtIso))
}

function formatDateLabel(dateStr) {
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return dateStr

  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

function DailyReportOffcanvas({ open, onClose, loading, title, children }) {
  if (!open) return null

  const root = document.body
  return createPortal(
    <>
      <div
        className="offcanvas-backdrop fade show"
        role="presentation"
        onClick={onClose}
      />
      <div
        className="offcanvas offcanvas-end w-50 show"
        tabIndex={-1}
        style={{ visibility: 'visible' }}
        role="dialog"
        aria-modal="true"
      >
        <div className="offcanvas-header">
          <div className="flex-column">
            <h5 className="offcanvas-title">{loading ? 'Loading…' : title}</h5>
          </div>
          <button type="button" className="btn-close text-reset" aria-label="Close" onClick={onClose} />
        </div>
        <div className="offcanvas-body">{children}</div>
      </div>
    </>,
    root,
  )
}

const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export default function Index({ dailyReports, initialUserId, initialDailyReportId }) {
  const [rawData, setRawData] = useState(dailyReports)
  const loading = false
  const error = null
  const [selectedUserId, setSelectedUserId] = useState(initialUserId || null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [onlineUsers, setOnlineUsers] = useState([])

  const [offcanvasOpen, setOffcanvasOpen] = useState(false)
  const [offcanvasLoading, setOffcanvasLoading] = useState(false)
  const [offcanvasTitle, setOffcanvasTitle] = useState('')
  const [offcanvasContentHtml, setOffcanvasContentHtml] = useState('')

  const data = useMemo(() => applyUserFilterToData(rawData, selectedUserId), [rawData, selectedUserId])

  const didOpenInitialReport = useRef(false)
  const checklistRef = useRef(null)

  const daysByDate = useMemo(() => {
    if (!data?.days) return new Map()
    const m = new Map()
    data.days.forEach((d) => m.set(d.date, d))
    return m
  }, [data])

  const selectedDay = selectedDate ? daysByDate.get(selectedDate) : null

  const calendarCells = useMemo(() => {
    if (!data) return []
    return buildMonthGrid(data.year, data.month, daysByDate)
  }, [data, daysByDate])

  const onlineUserIds = useMemo(() => new Set((onlineUsers || []).map((u) => String(u.id))), [onlineUsers])

  useEffect(() => {
    initTooltips(document)
  }, [rawData, onlineUsers, selectedDate])

  useEffect(() => {
    if (!initialDailyReportId) return
    if (!rawData) return
    if (didOpenInitialReport.current) return
    didOpenInitialReport.current = true
    handleOpenReport(initialDailyReportId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDailyReportId, rawData])

  useEffect(() => {
    let cancelled = false

    fetchOnlineUsers()
      .then((payload) => {
        if (cancelled) return
        setOnlineUsers(payload.users || [])
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (selectedDate && checklistRef.current) {
      checklistRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [selectedDate])

  async function handleOpenReport(id) {
    setOffcanvasOpen(true)
    setOffcanvasLoading(true)
    setOffcanvasTitle('')
    setOffcanvasContentHtml('')

    const payload = await fetchDailyReport({ id })
    const dr = payload.daily_report

    const dateLabel = new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(dr.applicable_date))
    const submitted = submittedAtLabel(new Date(dr.applicable_date), dr.created_at)
    setOffcanvasTitle(`Daily Report for ${dateLabel} submitted ${submitted}`)
    setOffcanvasContentHtml(dr.content_html)
    setOffcanvasLoading(false)

    // Update in-memory state for "realtime" Seen By updates
    setRawData((prev) => {
      if (!prev) return prev
      const nextDays = prev.days.map((day) => {
        if (day.date !== dr.applicable_date) return day
        const nextEntries = (day.entries || []).map((entry) => {
          if (!entry.report || String(entry.report.id) !== String(dr.id)) return entry
          return { ...entry, report: { ...entry.report, viewers: dr.viewers } }
        })
        const nextReports = (day.reports || []).map((r) => {
          if (String(r.id) !== String(dr.id)) return r
          return { ...r, viewers: dr.viewers }
        })
        const nextViewers = uniqueById(nextReports.flatMap((r) => r.viewers || []))
        return { ...day, entries: nextEntries, reports: nextReports, viewers: nextViewers }
      })
      return { ...prev, days: nextDays }
    })
  }

  function handleDateClick(dateStr) {
    if (isFutureDateStr(dateStr)) return
    setSelectedDate(dateStr)
  }

  function navigateMonth(month, year) {
    router.visit(`/managers/daily_reports?month=${month}&year=${year}`)
  }

  if (loading) return <div className="container">Loading…</div>
  if (error) return <div className="container">{error}</div>
  if (!data) return <div className="container">No data</div>

  return (
    <>
      <div className="container-fluid">
        <div className="header">
          <div className="container-fluid">
            <div className="header-body">
              <div className="row align-items-end">
                <div className="col">
                  <h6 className="header-pretitle">Daily Reports</h6>
                  <h1 className="header-title">Daily Reports Checklist</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        {/* Month Navigation */}
        <div className="container mb-4">
          <div className="d-flex align-items-center justify-content-between">
            <button
              type="button"
              className="btn btn-white"
              onClick={() => navigateMonth(data.prev_month.month, data.prev_month.year)}
            >
              &larr; Previous
            </button>
            <h2 className="m-0">{data.month_label}</h2>
            <button
              type="button"
              className="btn btn-white"
              onClick={() => navigateMonth(data.next_month.month, data.next_month.year)}
            >
              Next &rarr;
            </button>
          </div>
        </div>

        {/* Month Calendar Grid */}
        <div className="container mb-4">
          <div
            className="m-auto"
            style={{
              display: 'grid',
              gap: '0.5rem',
              gridTemplateColumns: 'repeat(7, 1fr)',
            }}
          >
            {WEEKDAY_HEADERS.map((h) => (
              <div key={h} className="text-center p-2">
                <strong>{h}</strong>
              </div>
            ))}
            {calendarCells.map((cell, index) => {
              if (cell.isPadding) {
                return (
                  <div
                    key={`pad-${index}`}
                    className="border p-2 text-muted opacity-50"
                    style={{ minHeight: '6rem' }}
                  >
                    <span className="small">{cell.dayNumber}</span>
                  </div>
                )
              }

              const day = cell.day
              const reportsCount = day?.reports_count || 0
              const expectedReportsCount = day?.expected_reports_count || 0
              const viewers = day?.viewers || []
              const isSelected = selectedDate === cell.date

              const isFiltered = !!selectedUserId
              const percentage = expectedReportsCount > 0
                ? Math.round((reportsCount / expectedReportsCount) * 100)
                : 0
              const ratioLabel = isFiltered
                ? `${reportsCount} report${reportsCount !== 1 ? 's' : ''}`
                : `${reportsCount}/${expectedReportsCount} (${percentage}%)`

              return (
                <button
                  key={cell.date}
                  type="button"
                  disabled={cell.isFuture}
                  className={`border p-2 text-start ${isSelected ? 'border-primary bg-primary-subtle' : ''} ${cell.isFuture ? 'opacity-50' : ''} ${cell.isWeekend && !isSelected ? 'bg-dark-subtle' : ''}`}
                  style={{ minHeight: '6rem' }}
                  onClick={() => handleDateClick(cell.date)}
                >
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className={`fw-bold ${cell.isToday ? 'text-primary' : ''}`}>{cell.dayNumber}</span>
                    {cell.isToday ? <span className="badge bg-primary">Today</span> : null}
                  </div>
                  {!cell.isFuture ? (
                    <>
                      <span className="small">{ratioLabel}</span>
                      {viewers.length > 0 ? (
                        <div className="mt-1">
                          <AvatarGroup users={viewers} max={3} />
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </button>
              )
            })}
          </div>
        </div>

        {/* Mobile date selector */}
        <div className="d-lg-none container mb-4">
          <div className="row justify-content-center">
            <div className="col-12 col-md-10">
              <select
                className="form-select"
                value={selectedDate || ''}
                onChange={(e) => {
                  const value = e.target.value
                  if (value && isFutureDateStr(value)) return
                  setSelectedDate(value || null)
                }}
              >
                <option value="">Select a date…</option>
                {data.days
                  .filter((day) => !isFutureDateStr(day.date))
                  .map((day) => {
                    const reportsCount = day.reports_count || 0
                    const expectedCount = day.expected_reports_count || 0
                    const percentage = expectedCount > 0
                      ? Math.round((reportsCount / expectedCount) * 100)
                      : 0
                    return (
                      <option key={day.date} value={day.date}>
                        {day.label} ({reportsCount}/{expectedCount} ({percentage}%))
                      </option>
                    )
                  })}
              </select>
            </div>
          </div>
        </div>

        {/* Filter */}
        {data.users?.length > 0 ? (
          <div className="container mb-4">
            <div className="avatar-filter m-auto">
              <h2>Filter</h2>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {data.users.map((u) => {
                  const active = selectedUserId && String(selectedUserId) === String(u.id)
                  return (
                    <button
                      key={u.id}
                      type="button"
                      className={`btn p-0 border-0 bg-transparent ${active ? 'is-active' : ''}`}
                      onClick={() => setSelectedUserId(active ? null : u.id)}
                      style={{ padding: 0 }}
                    >
                      <Avatar user={u} size="sm" online={onlineUserIds.has(String(u.id))} />
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        ) : null}

        {/* Checklist for selected date */}
        <div ref={checklistRef} className="container-fluid">
          <div className="row justify-content-center">
            {!selectedDay ? (
              <div className="col-12 col-md-10 col-xl-8">
                <div className="card">
                  <div className="card-body text-center text-muted py-5">
                    <h4 className="m-0">Select a date from the calendar to view the checklist</h4>
                  </div>
                </div>
              </div>
            ) : (
              <div className="col-12 col-md-10 col-xl-8">
                <div className="card mb-4">
                  <div className="card-header">
                    <h3 className="m-0">
                      {selectedDay.label}
                      <span className="text-muted fs-6 ms-2">({daysAgoLabel(selectedDay.date)})</span>
                    </h3>
                  </div>
                  <div className="card-body">
                    <ChecklistTable
                      entries={selectedDay.entries?.filter((e) => e.user?.is_daily_report_required)}
                      title="Daily Report Required"
                      onOpenReport={handleOpenReport}
                      selectedDayDate={selectedDay.date}
                    />
                    <ChecklistTable
                      entries={selectedDay.entries?.filter((e) => !e.user?.is_daily_report_required)}
                      title="Daily Report Not Required"
                      onOpenReport={handleOpenReport}
                      selectedDayDate={selectedDay.date}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <DailyReportOffcanvas
        open={offcanvasOpen}
        loading={offcanvasLoading}
        title={offcanvasTitle}
        onClose={() => setOffcanvasOpen(false)}
      >
        {offcanvasLoading ? (
          <div>Loading…</div>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: offcanvasContentHtml }} />
        )}
      </DailyReportOffcanvas>
    </>
  )
}
